// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Login gateway  (route /welcome — mobile number → SMS code → guest option)
// Six-box one-time-code input: auto-advance, backspace, arrow keys, paste, and auto-submit on
// the sixth digit. Digits only (ASCII 0-9).
// ─────────────────────────────────────────────────────────────────────────────
import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";

const LENGTH = 6;

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Fired once, when the 6th digit is entered by typing, pasting or autofill. */
  onComplete?: (value: string) => void;
  /** Read-only rather than disabled, so focus isn't dropped while a check is running. */
  busy?: boolean;
  invalid?: boolean;
  describedBy?: string;
}

/**
 * Six single-digit boxes that behave like one field: typing advances, Backspace steps back,
 * arrows move, and pasting (or the browser/OS one-time-code autofill) fills all six.
 */
export default function OtpInput({ value, onChange, onComplete, busy, invalid, describedBy }: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? "");

  const focusBox = (index: number) => {
    const box = refs.current[Math.max(0, Math.min(LENGTH - 1, index))];
    box?.focus();
    box?.select();
  };

  /** Write `incoming` digits starting at `start`, keeping everything before it. */
  const fill = (start: number, incoming: string) => {
    const clean = incoming.replace(/\D/g, "");
    if (!clean) return;
    const next = (value.slice(0, start) + clean).slice(0, LENGTH);
    onChange(next);
    focusBox(Math.min(next.length, LENGTH - 1));
    if (next.length === LENGTH) onComplete?.(next);
  };

  const handleChange = (index: number, raw: string) => {
    if (raw === "") {
      // Deleting a digit also drops anything typed after it, so the string never has gaps.
      onChange(value.slice(0, index));
      return;
    }
    fill(index, raw);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "Backspace":
        if (!digits[index] && index > 0) {
          event.preventDefault();
          onChange(value.slice(0, index - 1));
          focusBox(index - 1);
        }
        break;
      case "ArrowLeft":
        event.preventDefault();
        focusBox(index - 1);
        break;
      case "ArrowRight":
        event.preventDefault();
        focusBox(index + 1);
        break;
    }
  };

  const handlePaste = (index: number, event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    fill(index, event.clipboardData.getData("text"));
  };

  return (
    <div role="group" aria-label="6-digit code" aria-describedby={describedBy} className="flex justify-between gap-2 sm:gap-3">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            refs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          // The whole code is offered to the first box, so OS/browser autofill lands here and
          // handleChange spreads it across the boxes.
          autoComplete={index === 0 ? "one-time-code" : "off"}
          maxLength={LENGTH}
          value={digit}
          readOnly={busy}
          aria-busy={busy || undefined}
          aria-label={`Digit ${index + 1} of ${LENGTH}`}
          aria-invalid={invalid || undefined}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          onFocus={(e) => e.target.select()}
          className={`h-14 w-full min-w-0 rounded-xl border bg-white text-center font-display text-2xl font-bold text-ocean-950 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-teal-600 read-only:opacity-70 dark:bg-ocean-800 dark:text-foam-50 sm:h-16 ${
            invalid
              ? "border-coral-500 dark:border-coral-300"
              : digit
                ? "border-teal-600"
                : "border-ocean-800/55 dark:border-foam-100/50"
          }`}
        />
      ))}
    </div>
  );
}
